import type { TimeframeType } from '@/components/CommonFields/Timeframe';
import type { ACCOUNTING_CLASS, EntityType, UI_STATE_KEYS } from '@/consts';
import type { Dayjs, QUnitType } from '@/modules/dayjs';
import type { Toast, ToastOptions } from 'react-hot-toast';
import type { Attachment } from '@/modules/attachment';
import type { Dimension, DimensionValue } from '@/modules/dimension';
import type { PeriodUnitKey } from '@/utils/period';

/** Response of a server action that will be used across system for same response */
export type ServerActionResponse =
  | {
      /**
       * If true, the page will be refreshed using NextJS refresh mechanism.
       * It's not as same as full page refresh, but rather background one.
       * */
      refresh?: boolean;
      /**
       * If true, the page will be redirected to the previous page
       * @note useful in case we have Intercepted routes which needs to return to prev page after action
       * */
      redirectBack?: boolean;
      /**
       * If defined, the page will be redirected to the given path on success
       * @note use this when redirecting from one modal to another
       * */
      redirect?: string;
      /**
       * Same as redirect, but replace does not create entry in browser history
       * @note use this when redirecting from one modal to another
       * */
      replace?: string;
      /**
       * Errors object to be set in formik
       * @note needs to follow formik errors object structure
       * */
      errors?: object;
      /** Status code of the error */
      errorStatus?: number;
      /** Reset form state to initial */
      resetForm?: boolean;
      /** New values that form will be updated with after reset. */
      resetFormValues?: object;
      /** Toast message to be shown */
      toast?: {
        // `warning` is not a react-hot-toast type but the app's toast wrapper
        // (Toast.utils) implements it via AlertVariants, and useServerAction
        // dispatches through that wrapper.
        type: Toast['type'] | 'warning';
        message: string;
        /** Optional secondary text shown under the toast title (expandable). */
        description?: string;
      } & ToastOptions;
      data?: any;
      attachmentsIds?: Attachment['attachment_id'][];
      downloadBlob?: {
        data: number[];
        filename: string;
        mimeType: string;
      };
      /**
       * SWR cache tags to invalidate on success.
       * These are already-populated tags (no placeholders).
       */
      invalidateTags?: string[];
    }
  /** in case server actions redirects the return type is void */
  | void;

export type ServerAction = (
  values: any,
  responseOpts?: ServerActionResponse,
) => Promise<ServerActionResponse>;

export type ApiMethods = 'GET' | 'POST' | 'PUT' | 'DELETE';

export enum EntityEnum {
  PROVIDER = 'PROVIDER',
  ORG_PROVIDER = 'ORG_PROVIDER',
  TASK = 'TASK',
  SUBSIDIARY = 'SUBSIDIARY',
  FLUX_BUSINESS_CONTEXT = 'FLUX_BUSINESS_CONTEXT',
  /** A reconciliation source file (lead sheet) uploaded straight to storage. */
  EXTERNAL_BALANCE_SOURCE = 'EXTERNAL_BALANCE_SOURCE',
}

export enum OrderEnum {
  ASC = 'asc',
  DESC = 'desc',
}

export enum StatusEnum {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  EMPTY = 'EMPTY',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  DONE = 'DONE',
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  CANCELLED = 'CANCELLED',
  IN_PROGRESS = 'IN_PROGRESS',
  ADJUSTMENTS_IN_PROGRESS = 'ADJUSTMENTS_IN_PROGRESS',
  SYNCING = 'SYNCING',
  SYNCING_GROUPS = 'SYNCING_GROUPS',
  IN_REVIEW = 'IN_REVIEW',
  FAILED = 'FAILED',
  FAILURE = 'FAILURE',
  SUBMITTED = 'SUBMITTED',
  POSTED = 'POSTED',
  DRAFT = 'DRAFT',
  WARNING = 'WARNING',
  NOT_STARTED = 'NOT_STARTED',
  PAST_DUE = 'PAST_DUE',
  NO_OUTPUT = 'NO_OUTPUT',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
  DISMISSED = 'DISMISSED',
  NEW = 'NEW',
  RESOLVED = 'RESOLVED',
  RESETING = 'RESETING',
}

export enum PeriodEnum {
  ALL_DATES = 'ALL_DATES',
  TTM = 'TTM',
  THIS_WEEK = 'THIS_WEEK',
  THIS_MONTH = 'THIS_MONTH',
  THIS_QUARTER = 'THIS_QUARTER',
  THIS_YEAR = 'THIS_YEAR',
  LAST_WEEK = 'LAST_WEEK',
  LAST_MONTH = 'LAST_MONTH',
  LAST_QUARTER = 'LAST_QUARTER',
  LAST_YEAR = 'LAST_YEAR',
  NEXT_30_DAYS = 'NEXT_30_DAYS',
  NEXT_60_DAYS = 'NEXT_60_DAYS',
  NEXT_90_DAYS = 'NEXT_90_DAYS',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_60_DAYS = 'LAST_60_DAYS',
  LAST_90_DAYS = 'LAST_90_DAYS',
  CUSTOM = 'CUSTOM',
  ANNUAL = 'Annual',
  MONTHLY = 'Monthly',
}

export type FileDownloadResponse = {
  name: string;
  attachment_id: string;
  pre_signed_url: string;
  url: string;
};

export type EntityRef = {
  entity_id: string;
  entity_type: EntityType;
  entity_name?: string;
};

export type OptionValue = string | number;

export type Option = {
  value: OptionValue;
  name: string;
  icon?: React.ReactNode;
};

export interface Pagination {
  page: number;
  pageSize: number;
}

export interface PaginationWithQueryString extends Pagination {
  q?: string;
}

export interface Sort {
  field: string;
  sort: string;
}

export interface PaginationPayload {
  page: string;
  per_page: string;
}

export interface SortPayload {
  sort: string;
  order: string;
}

export interface PaginationResponse {
  page: number;
  per_page: number;
  pages: number;
  total: number;
  include_total?: boolean;
}

export type PaginatedResults<T = any> = {
  pagination: PaginationResponse;
  results: T[];
};

export type PaginatedResultsArray<T = any> = [T[], PaginationResponse];

export type DayjsOrNull = Dayjs | string | number | null;

export type DateValue = string | null;

export type DateRange = [DateValue, DateValue];

export interface PaginationQuery<SortFields = string> {
  page?: string;
  per_page?: string;
  sort?: SortFields;
  order?: 'asc' | 'desc';
  q?: string;
}

export enum PageEnum {
  TASKS = 'tasks',
  BILLS = 'bills',
  NOMINAL_LEDGER = 'nominal-ledger',
  GENERAL_LEDGER = 'general-ledger',
  EXCHANGE_RATES = 'exchange-rates',
  MATCHING = 'auto-ict',
  ACTIVITY = 'activity',
}

export type ElementPosition = {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
  width?: number;
  height?: number;
};

export type EntityReferenceWithId = {
  entityType: EntityType;
  entityId: string | number;
};

export type EntityReferenceWithData<DataType = unknown> = {
  entityType: EntityType;
  entityData: DataType;
};

export type EntityReference<DataType = unknown> =
  | EntityReferenceWithId
  | EntityReferenceWithData<DataType>;

export type DateUnit = QUnitType;

export type UIStateKeys = (typeof UI_STATE_KEYS)[keyof typeof UI_STATE_KEYS];

export type AccountingClassType =
  (typeof ACCOUNTING_CLASS)[keyof typeof ACCOUNTING_CLASS];

export type ServerPeriodLimitation = {
  max_range?: number;
  available_past_periods?: number;
  available_future_periods?: number;
  // Client-only: caps every unit at the end of the fiscal year N years ahead
  available_future_fiscal_years?: number;
  by_last_closed_period?: boolean;
};

export type ServerDateLimitations = Record<
  PeriodUnitKey,
  ServerPeriodLimitation
>;

export type PeriodLimitation = {
  minDate?: DateValue;
  maxDate?: DateValue;
  disablePast?: boolean;
  maxRange?: number;
  hideOutOfRangePeriods?: boolean;
};

export type ServerTimeframeLimitations = Record<
  DateUnit,
  ServerPeriodLimitation
>;

export type DateLimitations = Record<DateUnit, PeriodLimitation>;

export type TimeframeValue = {
  tf_type: TimeframeType;
  periods?: number;
  offset?: number;
  unit: QUnitType;
  from_date?: DateValue;
  to_date?: DateValue;
};

export type DimensionValues = {
  dimension_id?: Dimension['id'];
  dimension_value_ids?: DimensionValue['id'][];
};

export type ImmutableState = {
  immutable: boolean;
  immutableReason?: string;
};
